<template>
  <section name="presentation-theme" class="relative p-4 flex flex-col gap-4 pt-18" v-aha-emit-action.view>
    <section v-if="canCreateTheme" class="flex flex-col gap-4">
      <section class="flex justify-between items-center">
        <label class="label-option font-bold">{{ $i18nPresenter.t('My Themes') }}</label>
        <aha-button @click="isShowConfigModal = true" variant="base">
          <aha-icon
            name="add"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            class="mr-2"
          />
          {{ $i18nPresenter.t('Create') }}
        </aha-button>
      </section>
      <section class="grid grid-cols-2 gap-4 themes">
        <ThemeThumbnail
          v-for="theme in themeList"
          :key="theme.id"
          :theme="theme"
          @clickTheme="setTheme(theme)"
          class="relative flex flex-col gap-2"
          :class="{'active': theme.id == currentPresentationTheme?.themeId}"
          v-hover-intent="{
            onEnter: () => previewTheme(theme),
            onExit: () => removePreview(theme)
          }"
        >
          <template #context-menu-items>
            <aha-context-menu-item @click="showDeleteThemeModal(theme)">
              <div>{{ $i18nPresenter.t('Delete') }}</div>
            </aha-context-menu-item>
          </template>
        </ThemeThumbnail>
      </section>
    </section>
    <div v-if="canCreateTheme" class="divider bg-secondary-20 h-px"></div>
    <section class="flex flex-col gap-4">
      <label class="label-option font-bold">{{ $i18nPresenter.t('Official Themes') }}</label>
      <section class="grid grid-cols-2 gap-4 themes">
        <ThemeThumbnail
          v-for="theme in officialThemes"
          :key="theme.id"
          :theme="theme"
          @clickTheme="setTheme(theme)"
          :showContextMenu="false"
          class="relative flex flex-col gap-2"
          :class="{'active': theme.id == currentPresentationTheme?.themeId}"
          v-hover-intent="{
            onEnter: () => previewTheme(theme),
            onExit: () => removePreview(theme)
          }"
        >
        </ThemeThumbnail>
      </section>
      <aha-modal
        v-if="isShowConfigModal"
        size="5xl"
        hideHeader
        noMarginPadding
        @hide="isShowConfigModal = false"
      >
        <ThemeConfig
          :colorPalette="presetColorPalettes?.[0]"
          @saveTheme="saveTheme"
        />
      </aha-modal>
      <aha-modal
        v-if="isShowDeleteThemeModal"
        @hide="isShowDeleteThemeModal = false"
        :title="$i18nPresenter.t('Are you sure you want to delete this theme?')"
      >
        <h3>
          "{{ removingTheme.themeName }}"
        </h3>
        <template #action>
          <aha-button icon-name="delete" variant="danger" @click="deleteTheme(removingTheme)">
            {{ $i18nPresenter.t('Delete') }}
          </aha-button>
        </template>
      </aha-modal>
    </section>
  </section>
</template>

<script>
import {
  mapActions, mapGetters, mapState, mapMutations,
} from 'vuex'
import { omit } from 'lodash'
import DesignEditPanel from '@/components/presentations/types/DesignEditPanel.vue'
import ThemeConfig from '@/components-v2/presentation-editor/side-right/right-nav/ThemeConfig.vue'
import ThemeThumbnail from '@/components-v2/presentation-editor/side-right/right-nav/ThemeThumbnail.vue'
import Utils from '@/util'

export default {
  name: 'ThemeSetting',
  components: {
    ThemeConfig,
    ThemeThumbnail,
  },
  extends: DesignEditPanel,
  data() {
    return {
      isShowConfigModal: false,
      removingTheme: null,
      isShowDeleteThemeModal: false,
      // For Preview Theme Logo
      tempPresentationLogo: null,
      tempColorPalette: null,
    }
  },
  computed: {
    ...mapGetters('themeModule', ['themeList', 'officialThemes', 'currentPresentationTheme', 'canCreateTheme']),
    ...mapGetters('presetModule', ['presetColorPalettes']),
    ...mapGetters('presentationAttributeModule', ['presentationAttributeColorPalette']),
    ...mapState({
      presentation: (state) => state.presentation,
      slideActive: (state) => state.slideActive,
    }),
  },
  methods: {
    ...mapActions('themeModule', ['setThemeAction', 'createThemesAction', 'deleteThemeAction']),
    ...mapActions(['updatePresentationAction', 'updateSlideActiveAction']),
    ...mapMutations('presentationAttributeModule', ['updatePresentationAttribute']),
    _getColorPaletteFrom(theme) {
      if (!this.presetColorPalettes || !theme.colorPalette) return null
      return theme.colorPalette
    },
    async setTheme(theme) {
      const allSlides = this.presentation.slides
      this.selectTheme(theme, allSlides)
      this.setFontFromTheme(theme)
      this.setLogoFromTheme(theme)
      this.setColorPalleteFromTheme(theme)
      this.tempColorPalette = null
      this.setThemeAction({
        presentationId: this.presentation.id,
        ...theme,
        backgroundImage: theme.backgroundImage?.name,
        logo: theme.logo?.name,
        colorPalette: this._getColorPaletteFrom(theme),
        themeId: theme.id,
      })
      this.refreshActiveSlide()
    },
    async refreshActiveSlide() {
      // This is a hack to refresh the active slides
      // because the active slide is not updated when the theme is changed
      const slideActive = JSON.parse(JSON.stringify(this.slideActive))
      this.updateSlideActiveAction({ type: 'freeStyle' })
      await this.$nextTick()
      this.updateSlideActiveAction({
        type: slideActive.type,
      })
    },

    async setColorPalleteFromTheme(theme) {
      if (!theme.colorPalette) return
      this.updatePresentationAttribute({
        attributeName: 'colorPalette',
        attributeValue: this._getColorPaletteFrom(theme),
      })
    },
    async setFontFromTheme(theme) {
      this.setFontFamily(theme.fontFamily)
      this.updatePresentationAction({
        fontFamily: theme.fontFamily,
      })
    },
    async setLogoFromTheme(theme) {
      if (!theme.logo) return
      this.updatePresentationAction({
        logo: theme.logo,
        notShowAhaSlidesLogo: false,
      })
      // Remove the logo from the preview theme cause it's already applied
      this.tempPresentationLogo = null
    },
    async previewTheme(theme) {
      const allSlides = this.presentation.slides
      this.handleHoverOnTheme(omit(theme, 'id'), 'over', allSlides)

      // Logo
      this.tempPresentationLogo = {
        logo: this.presentation.logo,
        notShowAhaSlidesLogo: this.presentation.notShowAhaSlidesLogo,
      }
      if (theme.logo) {
        this.updatePresentationAction({
          logo: theme.logo,
          notShowAhaSlidesLogo: false,
        })
      }

      this.tempColorPalette = this.presentationAttributeColorPalette
      this.setColorPalleteFromTheme(theme)

      this.testFontFamily(theme.fontFamily)
      this.refreshActiveSlide()
    },
    async removePreview(theme) {
      const allSlides = this.presentation.slides
      this.handleHoverOnTheme(omit(theme, 'id'), 'out', allSlides)

      if (this.tempPresentationLogo) {
        this.updatePresentationAction({
          ...this.tempPresentationLogo,
        })
      }

      this.setColorPalleteFromTheme({
        colorPalette: this.tempColorPalette,
      })
      this.testFontFamily(this.presentation.fontFamily)
      this.refreshActiveSlide()
    },
    async saveTheme(theme) {
      await this.createThemesAction(theme)
      this.isShowConfigModal = false
      Utils.showToastSuccess(this.$i18nPresenter.t('New theme saved'))
    },
    async showDeleteThemeModal(theme) {
      this.removingTheme = theme
      this.isShowDeleteThemeModal = true
    },
    async deleteTheme(theme) {
      this.isShowDeleteThemeModal = false
      await this.deleteThemeAction(theme.id)
      Utils.showToastSuccess(this.$i18nPresenter.t('Theme deleted'))
    },
  },
}
</script>

<style lang="scss" scoped>
.active {
  @apply rounded;
  outline: 3px solid #348EF6;
}
</style>
